import { auth } from '../firebase';
import {
  AppVisibilitySettings,
  AppVisibilitySettingsUpdate,
  DEFAULT_APP_VISIBILITY_SETTINGS,
} from '../types/appVisibility';

const API_BASE_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api`;

class AppVisibilityService {
  private async getAuthHeaders(): Promise<Record<string, string>> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    const token = await user.getIdToken();
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || 'Request failed');
    }

    return response.json();
  }

  /**
   * Get per-app visibility settings for a device
   */
  async getSettings(deviceId: string): Promise<AppVisibilitySettings> {
    try {
      return await this.request<AppVisibilitySettings>(
        `/devices/${deviceId}/app-visibility/`
      );
    } catch {
      // Return defaults (everything visible) if settings don't exist yet
      return DEFAULT_APP_VISIBILITY_SETTINGS;
    }
  }

  /**
   * Update per-app visibility for a device. Only the provided keys change.
   */
  async updateSettings(
    deviceId: string,
    updates: AppVisibilitySettingsUpdate
  ): Promise<AppVisibilitySettings> {
    return this.request<AppVisibilitySettings>(
      `/devices/${deviceId}/app-visibility/`,
      {
        method: 'PUT',
        body: JSON.stringify(updates),
      }
    );
  }
}

export const appVisibilityService = new AppVisibilityService();
